import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';

const isDomainFormat = (value) => /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value);

const isGuid = (value) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
        value,
    );

const lookupTenantByDomain = async (domain) => {
    try {
        const response = await fetch(
            `https://login.microsoftonline.com/${domain}/.well-known/openid-configuration`,
        );
        if (!response.ok) return null;
        const result = await response.json();
        const match = result.token_endpoint?.match(/\/([^/]+)\/oauth2/);
        return match ? match[1] : null;
    } catch {
        return null;
    }
};

/**
 * Manages tenant/domain form state shared between AddProvider and EditMicrosoftSSO.
 */
export function useTenantDomainForm() {
    const [inputValue, setInputValue] = useState('');
    const [inputStatus, setInputStatus] = useState('idle'); // 'idle' | 'loading' | 'error'
    const [inputError, setInputError] = useState('');
    const [tenantIds, setTenantIds] = useState([]);
    const [domains, setDomains] = useState([]);
    const [showDomainRestriction, setShowDomainRestriction] = useState(false);
    const [domainInputValue, setDomainInputValue] = useState('');
    const [domainInputError, setDomainInputError] = useState('');

    const handleInputChange = useCallback(
        (value) => {
            setInputValue(value);
            if (inputStatus === 'error') {
                setInputStatus('idle');
                setInputError('');
            }
        },
        [inputStatus],
    );

    const handleDomainInputChange = useCallback((value) => {
        setDomainInputValue(value);
        setDomainInputError('');
    }, []);

    const handleAddAction = useCallback(async () => {
        const value = inputValue.trim();
        if (!value) return;

        setInputError('');
        setInputStatus('loading');

        try {
            if (isGuid(value)) {
                if (tenantIds.includes(value)) {
                    setInputError('This Tenant ID already exists');
                    setInputStatus('error');
                    return;
                }
                setTenantIds((prev) => [value, ...prev]);
                setInputValue('');
                setInputStatus('idle');
                return;
            }

            if (isDomainFormat(value)) {
                if (!domains.includes(value)) {
                    setDomains((prev) => [value, ...prev]);
                }
                const tenantId = await lookupTenantByDomain(value);
                if (tenantId) {
                    if (!tenantIds.includes(tenantId)) {
                        setTenantIds((prev) => [tenantId, ...prev]);
                        toast.success(`Found tenant: ${tenantId}`);
                    } else {
                        toast.info(`Tenant for ${value} already added`);
                    }
                } else {
                    toast.warning(`Could not find tenant for domain: ${value}`);
                }
                setInputValue('');
                setInputStatus('idle');
                return;
            }

            if (tenantIds.includes(value)) {
                setInputError('This Tenant ID already exists');
                setInputStatus('error');
                return;
            }
            setTenantIds((prev) => [value, ...prev]);
            setInputValue('');
            setInputStatus('idle');
        } catch {
            setInputError('Failed to process input. Please try again.');
            setInputStatus('error');
        }
    }, [inputValue, tenantIds, domains]);

    const handleAddDomain = useCallback(() => {
        const value = domainInputValue.trim();
        if (!value) return;
        if (!isDomainFormat(value)) {
            setDomainInputError('Invalid domain format');
            return;
        }
        if (domains.includes(value)) {
            setDomainInputError('This domain already exists');
            return;
        }
        setDomains((prev) => [value, ...prev]);
        setDomainInputValue('');
        setDomainInputError('');
    }, [domainInputValue, domains]);

    const removeTenant = useCallback(
        (id) => setTenantIds((prev) => prev.filter((t) => t !== id)),
        [],
    );

    const removeDomain = useCallback(
        (domain) => setDomains((prev) => prev.filter((d) => d !== domain)),
        [],
    );

    /** Reset all form state (call on modal close). */
    const reset = useCallback(() => {
        setInputValue('');
        setInputStatus('idle');
        setInputError('');
        setTenantIds([]);
        setDomains([]);
        setShowDomainRestriction(false);
        setDomainInputValue('');
        setDomainInputError('');
    }, []);

    /** Seed form state from existing provider data (edit mode). */
    const populate = useCallback((tenants, emailDomains, isBoth) => {
        setTenantIds((tenants || []).filter(Boolean));
        setDomains((emailDomains || []).filter(Boolean));
        setShowDomainRestriction(isBoth === true);
    }, []);

    return {
        inputValue,
        inputStatus,
        inputError,
        tenantIds,
        domains,
        showDomainRestriction,
        setShowDomainRestriction,
        domainInputValue,
        domainInputError,
        handleInputChange,
        handleDomainInputChange,
        handleAddAction,
        handleAddDomain,
        removeTenant,
        removeDomain,
        isFormValid:
            (tenantIds.length > 0 || domains.length > 0) &&
            (!showDomainRestriction ||
                (tenantIds.length > 0 && domains.length > 0)),
        reset,
        populate,
    };
}
